// RANGO DE FECHAS: un solo control para «desde» y «hasta» (decisión del operador, 5-oct-2026).
//
// QUÉ REEMPLAZA. Cada rango de las bandejas eran dos `Input type="date"`: dos calendarios del
// navegador de un solo día, sin «desde» ni «hasta» a la vista y seis campos iguales en la
// bandeja general. Ahora es UN campo («Del 10/07/2026 al 16/07/2026») que abre UN calendario
// donde se elige el inicio y el fin, con atajos y con las dos fechas escribibles a mano.
//
// COMO SE ELIGE. El primer clic fija el inicio y el segundo el fin (si el segundo es anterior,
// se intercambian: no hay rango invertido por clic). El rango se APLICA al completarlo, con un
// atajo, con «Listo» o al hacer clic fuera; Escape lo descarta y deja el anterior. Mientras se
// elige, la lista no se recarga a medias.
//
// ESCRITO A MANO (HU-34-C2). Las fechas también se escriben dd/mm/aaaa. Un fin anterior al
// inicio no se aplica: se dice «La fecha final no puede ser anterior a la inicial.».
//
// TECLADO Y CAPAS (lineamiento, «Teclado»; HU-51). El panel va en un PORTAL con posición fija,
// como el `Select`: ninguna tarjeta con `overflow-hidden` lo recorta. Se apila como capa
// (`useCapaAbierta`): Escape cierra solo el calendario y el foco vuelve al campo. En la rejilla
// de días, las flechas mueven el foco (un solo día tabulable) y Enter o Espacio eligen.
//
// VALORES. Entran y salen en ISO (`aaaa-mm-dd`), como los `*_desde`/`*_hasta` del contrato: la
// pantalla no cambia lo que manda al servidor. Las fechas se arman con la hora LOCAL y nunca
// con `toISOString()`, que en Lima corre un día.
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ButtonHTMLAttributes, ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button, clasesCampo, useCapaAbierta } from '../ui';
import { comoFecha } from '../../lib/fechas';

/** El mismo texto con que el servidor rechaza el rango (HU-34-C2). */
export const TEXTO_RANGO_INVERTIDO = 'La fecha final no puede ser anterior a la inicial.';
const TEXTO_FECHA_MAL_ESCRITA = 'Escribe la fecha como dd/mm/aaaa.';

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre',
  'octubre', 'noviembre', 'diciembre'];
const DIAS_DE_LA_SEMANA: [string, string][] = [['lu', 'lunes'], ['ma', 'martes'], ['mi', 'miércoles'],
  ['ju', 'jueves'], ['vi', 'viernes'], ['sá', 'sábado'], ['do', 'domingo']];
/** Por debajo de este ancho de ventana se muestra un solo mes. */
const ANCHO_PARA_DOS_MESES = 640;

type Rango = { desde: string; hasta: string };

const dosCifras = (n: number) => String(n).padStart(2, '0');
/** `aaaa-mm-dd` con la hora local (no `toISOString`, que corre un día en Lima). */
export const aIso = (d: Date): string => `${d.getFullYear()}-${dosCifras(d.getMonth() + 1)}-${dosCifras(d.getDate())}`;
const deIso = (iso: string): Date => {
  const [a, m, d] = iso.split('-').map(Number);
  return new Date(a, m - 1, d);
};
const sumarDias = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const primeroDelMes = (d: Date, desplazamiento = 0) => new Date(d.getFullYear(), d.getMonth() + desplazamiento, 1);

/** `dd/mm/aaaa` a ISO; `null` si no es una fecha que exista (31/02 no existe). */
export function deFechaEscrita(texto: string): string | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(texto.trim());
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return d.getMonth() === Number(m[2]) - 1 && d.getDate() === Number(m[1]) ? aIso(d) : null;
}

/** El rango puesto en palabras, sin rayas (lineamiento): «Del 10/07/2026 al 16/07/2026». */
export function textoDelRango({ desde, hasta }: Rango): string {
  if (desde && hasta) return desde === hasta ? `El ${comoFecha(desde)}` : `Del ${comoFecha(desde)} al ${comoFecha(hasta)}`;
  if (desde) return `Desde el ${comoFecha(desde)}`;
  if (hasta) return `Hasta el ${comoFecha(hasta)}`;
  return '';
}

/** Los atajos del panel. `hoy` entra como argumento para que las pruebas fijen el día. */
export const ATAJOS: { etiqueta: string; rango: (hoy: Date) => [Date, Date] }[] = [
  { etiqueta: 'Hoy', rango: (h) => [h, h] },
  { etiqueta: 'Últimos 7 días', rango: (h) => [sumarDias(h, -6), h] },
  { etiqueta: 'Últimos 30 días', rango: (h) => [sumarDias(h, -29), h] },
  { etiqueta: 'Este mes', rango: (h) => [primeroDelMes(h), h] },
  { etiqueta: 'Mes anterior', rango: (h) => [primeroDelMes(h, -1), new Date(h.getFullYear(), h.getMonth(), 0)] },
  { etiqueta: 'Este año', rango: (h) => [new Date(h.getFullYear(), 0, 1), h] },
];

const ordenado = (a: string, b: string): Rango => (a && b && b < a ? { desde: b, hasta: a } : { desde: a, hasta: b });

export type PropsDeRangoDeFechas = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'value'> & {
  desde: string;
  hasta: string;
  /** Se avisa con el rango YA aplicado (ordenado, ISO, `''` = sin límite). */
  onCambio: (rango: Rango) => void;
  /** Lo que dice el campo vacío. */
  placeholder?: string;
  /**
   * Donde el campo no lleva etiqueta visible (los filtros de las bandejas priorizadas), el
   * rango puesto se lee con su nombre delante: «Recibido: del 10/07/2026 al 16/07/2026».
   */
  prefijo?: string;
  /** Solo para pruebas: el día con que se arman los atajos y el mes inicial. */
  hoy?: Date;
  /** De él salen los del panel: `-desde`, `-hasta`, `-error`, `-limpiar`, `-panel`. */
  'data-testid'?: string;
};

export default function RangoDeFechas({
  desde, hasta, onCambio, placeholder = 'Cualquier fecha', prefijo, hoy: hoyFijo, 'data-testid': testid, className = '', ...resto
}: PropsDeRangoDeFechas) {
  const [abierto, setAbierto] = useState(false);
  const [borrador, setBorrador] = useState<Rango>({ desde, hasta });
  const [escrito, setEscrito] = useState({ desde: '', hasta: '' });
  const [error, setError] = useState<string | null>(null);
  const [visto, setVisto] = useState(() => new Date());
  const [enfocado, setEnfocado] = useState('');
  const [sobre, setSobre] = useState('');
  const [dosMeses, setDosMeses] = useState(true);
  const caja = useRef<HTMLDivElement>(null);
  const disparador = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const idDelPanel = useId();
  const idDelError = useId();
  const capa = useCapaAbierta(abierto);
  const hoy = hoyFijo ?? new Date();
  const hoyIso = aIso(hoy);
  const t = (sufijo: string) => (testid ? `${testid}-${sufijo}` : undefined);

  const abrir = () => {
    const dos = window.innerWidth >= ANCHO_PARA_DOS_MESES;
    const base = desde ? deIso(desde) : hasta ? deIso(hasta) : hoy;
    // Con dos meses y sin rango, se ven el anterior y el actual: lo habitual es buscar hacia atrás.
    setVisto(primeroDelMes(base, dos && !desde && !hasta ? -1 : 0));
    setDosMeses(dos);
    setBorrador({ desde, hasta });
    setEscrito({ desde: comoFecha(desde), hasta: comoFecha(hasta) });
    setError(null);
    setSobre('');
    setEnfocado(desde || hasta || hoyIso);
    setAbierto(true);
  };

  const cerrar = useCallback((aplicar: boolean, devolverFoco = true) => {
    setAbierto(false);
    if (devolverFoco) disparador.current?.focus({ preventScroll: true });
    if (aplicar) {
      setBorrador((b) => {
        if (b.desde !== desde || b.hasta !== hasta) onCambio(b);
        return b;
      });
    }
  }, [desde, hasta, onCambio]);

  const aplicar = (rango: Rango) => {
    const r = ordenado(rango.desde, rango.hasta);
    setBorrador(r);
    setAbierto(false);
    disparador.current?.focus({ preventScroll: true });
    if (r.desde !== desde || r.hasta !== hasta) onCambio(r);
  };

  // Clic fuera: aplica lo elegido (sin rango invertido) y cierra, sin robar el foco a donde se pulsó.
  const cerrarActual = useRef(cerrar);
  useEffect(() => { cerrarActual.current = cerrar; }, [cerrar]);
  const errorActual = useRef(error);
  useEffect(() => { errorActual.current = error; }, [error]);
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e: MouseEvent) => {
      const destino = e.target as Node | null;
      if (destino && (caja.current?.contains(destino) || panel.current?.contains(destino))) return;
      cerrarActual.current(!errorActual.current, false);
    };
    document.addEventListener('mousedown', fuera);
    // Como el `Select`: «hay una capa encima», para que Escape no pliegue el menú lateral.
    document.body.dataset.modales = String(Number(document.body.dataset.modales || 0) + 1);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.body.dataset.modales = String(Math.max(0, Number(document.body.dataset.modales || 1) - 1));
    };
  }, [abierto]);

  // Posición fija bajo el campo; si no cabe a la derecha se corre hacia la izquierda, y si no
  // cabe abajo pero sí arriba, se abre hacia arriba. Las coordenadas se escriben en el propio
  // panel (no en un `style` de JSX, estándar §6): solo se conocen al medir la ventana.
  const ubicar = useCallback(() => {
    const r = caja.current?.getBoundingClientRect();
    const el = panel.current;
    if (!r || !el) return;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - el.offsetWidth - 8));
    const abajo = window.innerHeight - r.bottom - 8;
    const haciaArriba = el.offsetHeight > abajo && r.top - 8 > abajo;
    el.style.left = `${left}px`;
    el.style.top = haciaArriba ? '' : `${r.bottom + 6}px`;
    el.style.bottom = haciaArriba ? `${window.innerHeight - r.top + 6}px` : '';
  }, []);
  useLayoutEffect(() => { if (abierto) ubicar(); }, [abierto, ubicar, dosMeses, visto]);
  useEffect(() => {
    if (!abierto) return undefined;
    window.addEventListener('resize', ubicar);
    window.addEventListener('scroll', ubicar, true);
    return () => {
      window.removeEventListener('resize', ubicar);
      window.removeEventListener('scroll', ubicar, true);
    };
  }, [abierto, ubicar]);

  // Al abrir y al moverse con las flechas, el foco va al día enfocado.
  useEffect(() => {
    if (!abierto || !enfocado) return;
    panel.current?.querySelector<HTMLButtonElement>(`[data-dia="${enfocado}"]`)?.focus({ preventScroll: true });
  }, [abierto, enfocado, visto]);

  const elegirDia = (iso: string) => {
    if (!borrador.desde || borrador.hasta) {
      setBorrador({ desde: iso, hasta: '' });
      setEscrito({ desde: comoFecha(iso), hasta: '' });
      setError(null);
      setEnfocado(iso);
      return;
    }
    aplicar({ desde: borrador.desde, hasta: iso });
  };

  const escribir = (lado: keyof Rango) => (e: ChangeEvent<HTMLInputElement>) => {
    const texto = e.target.value;
    setEscrito((w) => ({ ...w, [lado]: texto }));
    if (!texto.trim()) {
      setBorrador((b) => ({ ...b, [lado]: '' }));
      setError(null);
      return;
    }
    // Se valida al completar las diez cifras: a medio escribir no se acusa nada.
    if (texto.trim().length < 10) return;
    const iso = deFechaEscrita(texto);
    if (!iso) { setError(TEXTO_FECHA_MAL_ESCRITA); return; }
    const siguiente = { ...borrador, [lado]: iso };
    if (siguiente.desde && siguiente.hasta && siguiente.hasta < siguiente.desde) {
      setBorrador(siguiente);
      setError(TEXTO_RANGO_INVERTIDO);
      return;
    }
    setError(null);
    setBorrador(siguiente);
    setVisto(primeroDelMes(deIso(iso), lado === 'hasta' && dosMeses ? -1 : 0));
  };

  const teclasDelPanel = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && capa.current) {
      e.preventDefault();
      e.stopPropagation();
      cerrar(false);
      return;
    }
    if (e.key === 'Tab') {
      // Salir del panel con el tabulador lo cierra y devuelve el foco al campo: el panel vive
      // en un portal al final del documento y el foco se perdería fuera del formulario.
      const enfocables = panel.current ? [...panel.current.querySelectorAll<HTMLElement>('button:not([disabled]), input')]
        .filter((el) => el.tabIndex !== -1) : [];
      const primero = enfocables[0];
      const ultimo = enfocables[enfocables.length - 1];
      if ((!e.shiftKey && document.activeElement === ultimo) || (e.shiftKey && document.activeElement === primero)) {
        e.preventDefault();
        cerrar(!error);
      }
      return;
    }
    const dia = (e.target as HTMLElement).dataset?.dia;
    if (!dia) return;
    const paso = ({ ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 } as Record<string, number>)[e.key];
    if (!paso) return;
    e.preventDefault();
    const destino = sumarDias(deIso(dia), paso);
    const primerVisible = visto;
    const ultimoVisible = new Date(visto.getFullYear(), visto.getMonth() + (dosMeses ? 2 : 1), 0);
    if (destino < primerVisible) setVisto(primeroDelMes(visto, -1));
    if (destino > ultimoVisible) setVisto(primeroDelMes(visto, 1));
    setEnfocado(aIso(destino));
    if (borrador.desde && !borrador.hasta) setSobre(aIso(destino));
  };

  // Las teclas del panel se escuchan en el nodo, como el `Modal` del catálogo: el panel es un
  // `div role="dialog"`, no un control, y no lleva `onKeyDown` (Sonar S6847, MG-ILE-1089). Se
  // vuelve a enganchar en cada pintado para que el manejador vea el estado de ese momento.
  useEffect(() => {
    const nodo = panel.current;
    if (!abierto || !nodo) return undefined;
    nodo.addEventListener('keydown', teclasDelPanel);
    return () => nodo.removeEventListener('keydown', teclasDelPanel);
  });

  const vacio = !desde && !hasta;
  const rangoEnPalabras = textoDelRango({ desde, hasta });
  const texto = vacio ? placeholder
    : prefijo ? `${prefijo}: ${rangoEnPalabras.charAt(0).toLowerCase()}${rangoEnPalabras.slice(1)}` : rangoEnPalabras;
  const atajoActivo = ATAJOS.findIndex((a) => {
    const [d, h] = a.rango(hoy);
    return aIso(d) === borrador.desde && aIso(h) === borrador.hasta;
  });
  const finVisible = borrador.hasta || (borrador.desde ? sobre : '');
  const [a, b] = borrador.desde && finVisible ? [borrador.desde, finVisible].sort((x, y) => x.localeCompare(y)) : [borrador.desde, borrador.desde];
  const instruccion = !borrador.desde ? 'Elige el día de inicio.' : !borrador.hasta ? 'Ahora elige el día final.' : textoDelRango(borrador);

  const mes = (primero: Date, indice: number) => {
    const huecos = (primero.getDay() + 6) % 7;
    const ultimo = new Date(primero.getFullYear(), primero.getMonth() + 1, 0).getDate();
    const dias = Array.from({ length: ultimo }, (_, i) => aIso(new Date(primero.getFullYear(), primero.getMonth(), i + 1)));
    const titulo = `${MESES[primero.getMonth()]} ${primero.getFullYear()}`;
    const anterior = indice === 0;
    const siguiente = indice === (dosMeses ? 1 : 0);
    return (
      <div key={titulo}>
        <div className="flex items-center justify-between h-8 mb-1">
          {anterior
            ? <Button variant="ghost" size="sm" icon={ChevronLeft} aria-label="Ver el mes anterior" onClick={() => setVisto(primeroDelMes(visto, -1))} />
            : <span className="w-8" aria-hidden="true" />}
          <span className="text-body font-semibold text-ink first-letter:uppercase" aria-live="polite">{titulo}</span>
          {siguiente
            ? <Button variant="ghost" size="sm" icon={ChevronRight} aria-label="Ver el mes siguiente" onClick={() => setVisto(primeroDelMes(visto, 1))} />
            : <span className="w-8" aria-hidden="true" />}
        </div>
        <div role="grid" aria-label={titulo} className="grid grid-cols-[repeat(7,2.25rem)]">
          <div role="row" className="contents">
            {DIAS_DE_LA_SEMANA.map(([corto, largo]) => (
              <span key={corto} role="columnheader" aria-label={largo} className="h-7 text-center text-micro font-semibold text-ink-soft leading-7">{corto}</span>
            ))}
          </div>
          <div role="row" className="contents">
            {Array.from({ length: huecos }, (_, i) => <span key={`h${i}`} role="gridcell" aria-hidden="true" />)}
            {dias.map((iso) => {
              const inicio = Boolean(a) && iso === a;
              const fin = Boolean(b) && iso === b;
              const dentro = Boolean(a && b) && iso > a && iso < b;
              const extremo = inicio || fin;
              return (
                <span key={iso} role="gridcell" aria-selected={extremo || dentro}
                  className={'h-9 ' + (dentro ? 'bg-marca/10 ' : '')
                    + (inicio && b && a !== b ? 'bg-gradient-to-r from-transparent from-50% to-marca/10 to-50% ' : '')
                    + (fin && a && a !== b ? 'bg-gradient-to-r from-marca/10 from-50% to-transparent to-50% ' : '')}>
                  <button type="button" data-dia={iso} tabIndex={iso === enfocado ? 0 : -1}
                    aria-label={`${Number(iso.slice(8))} de ${MESES[Number(iso.slice(5, 7)) - 1]} de ${iso.slice(0, 4)}`}
                    aria-current={iso === hoyIso ? 'date' : undefined}
                    onClick={() => elegirDia(iso)}
                    onMouseEnter={() => { if (borrador.desde && !borrador.hasta) setSobre(iso); }}
                    className={'mx-auto flex h-9 w-9 items-center justify-center rounded-full text-body tabular-nums cursor-pointer transition-colors '
                      + (extremo ? 'bg-marca text-white font-semibold ' : 'text-ink hover:bg-canvas ')
                      + (iso === hoyIso && !extremo ? 'ring-1 ring-inset ring-marca ' : '')}>
                    {Number(iso.slice(8))}
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div ref={caja} className="relative w-full">
      <button ref={disparador} type="button" {...resto} data-testid={testid}
        aria-haspopup="dialog" aria-expanded={abierto} aria-controls={abierto ? idDelPanel : undefined}
        onClick={() => (abierto ? cerrar(!error) : abrir())}
        className={clasesCampo({ 'aria-invalid': resto['aria-invalid'], className })
          + ' flex items-center gap-2 text-left cursor-pointer ' + (vacio ? '' : 'pr-10')}>
        <CalendarDays size={16} className="shrink-0 text-ink-soft" aria-hidden="true" />
        <span className={'min-w-0 truncate tabular-nums ' + (vacio ? 'text-placeholder' : 'text-ink')}>{texto}</span>
      </button>
      {!vacio && (
        <button type="button" aria-label="Quitar el rango de fechas" data-testid={t('limpiar')}
          onClick={() => { setBorrador({ desde: '', hasta: '' }); onCambio({ desde: '', hasta: '' }); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-md text-ink-soft hover:bg-canvas hover:text-ink cursor-pointer">
          <X size={14} aria-hidden="true" />
        </button>
      )}

      {abierto && createPortal(
        <div ref={panel} id={idDelPanel} role="dialog" aria-label="Elegir un rango de fechas" data-testid={t('panel')}
          className={'fixed z-[10000] bg-white border border-borde rounded-xl shadow-[0_18px_44px_-12px_rgba(10,74,120,0.32)] animate-fade-in flex '
            + (dosMeses ? 'flex-row' : 'flex-col max-w-[calc(100vw-1rem)]')}>
          <div className={'p-2.5 flex gap-0.5 ' + (dosMeses ? 'flex-col border-r border-hairline min-w-36' : 'flex-row flex-wrap border-b border-hairline')}>
            <p className={'text-meta font-semibold text-ink-soft px-2 ' + (dosMeses ? 'pt-1 pb-1.5' : 'w-full pb-1')}>Atajos</p>
            {ATAJOS.map((atajo, i) => (
              <button key={atajo.etiqueta} type="button" aria-pressed={i === atajoActivo}
                onClick={() => { const [d, h] = atajo.rango(hoy); aplicar({ desde: aIso(d), hasta: aIso(h) }); }}
                className={'text-left rounded-lg px-2.5 py-1.5 text-body cursor-pointer transition-colors '
                  + (i === atajoActivo ? 'bg-marca/10 font-semibold text-ink' : 'text-ink hover:bg-canvas')}>
                {atajo.etiqueta}
              </button>
            ))}
          </div>
          <div className="p-3">
            <div className="grid grid-cols-2 gap-2 mb-2.5">
              {(['desde', 'hasta'] as const).map((lado) => (
                <label key={lado} className="block">
                  <span className="block font-semibold text-meta text-ink-soft mb-1">{lado === 'desde' ? 'Desde' : 'Hasta'}</span>
                  <input value={escrito[lado]} onChange={escribir(lado)} inputMode="numeric" placeholder="dd/mm/aaaa"
                    maxLength={10} data-testid={t(lado)} aria-invalid={error ? true : undefined}
                    aria-describedby={error ? idDelError : undefined}
                    className={clasesCampo({ 'aria-invalid': error ? true : undefined }) + ' tabular-nums'} />
                </label>
              ))}
            </div>
            <div className="flex gap-5">
              {mes(visto, 0)}
              {dosMeses && mes(primeroDelMes(visto, 1), 1)}
            </div>
            <div className="mt-2.5 pt-2.5 border-t border-hairline flex flex-wrap items-center gap-2">
              {error
                ? <p id={idDelError} role="alert" data-testid={t('error')} className="flex-1 min-w-0 text-meta text-rose-700">{error}</p>
                : <p className="flex-1 min-w-0 text-meta text-ink-soft">{instruccion}</p>}
              <Button variant="secondary" size="sm" onClick={() => { setBorrador({ desde: '', hasta: '' }); setEscrito({ desde: '', hasta: '' }); setError(null); }}>
                Limpiar
              </Button>
              <Button size="sm" disabled={Boolean(error)} onClick={() => aplicar(borrador)} data-testid={t('listo')}>
                Listo
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
